import { PageEvent } from '@angular/material/paginator';
import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription, interval } from 'rxjs';
import { AsyncImportService } from '../../services/pbm/async-import-service';
import { switchMap, takeWhile } from 'rxjs/operators';
import { AsyncImportStatus, ImportStatus } from '../../models/domains/pbm/async-import/async-import-status';

@Component({
  selector: 'app-dialog-import-file',
  templateUrl: './dialog-import-file.component.html',
  styleUrls: ['./dialog-import-file.component.scss']
})
export class DialogImportFileComponent {
  storageData: ImportLog[] = []
  fileToUpload: File | null | undefined = null
  panelOpenState = false;
  pooling: Subscription[] = []
  readonly idenfifier: string

  paginatedErrors: any[] = [];
  pageSize = 1;
  currentPage = 0;

  errosPerRow: RowError[] = [];

  @Output() downloadTemplateClicked: EventEmitter<void> = new EventEmitter<void>();
  @Output() uploadFileClicked: EventEmitter<File> = new EventEmitter<File>();
  @Input() uploadFileFinished: EventEmitter<{ "key": string }> = new EventEmitter<{ "key": string }>();

  constructor(@Inject(MAT_DIALOG_DATA) public data: { key: string, title: string },
    private importsStatusService: AsyncImportService,
    private cdr: ChangeDetectorRef,
    private dialogRef: MatDialogRef<DialogImportFileComponent>) {
    if (!data.key) {
      throw "Need key"
    }
    this.idenfifier = `imports:${data.key}`
  }

  ngOnDestroy(): void {
    localStorage.setItem(this.idenfifier, JSON.stringify(this.storageData));

    this.pooling.forEach(x => x.unsubscribe())
  }

  ngOnInit(): void {
    this.storageData = JSON.parse(localStorage.getItem(this.idenfifier) ?? "[]") as ImportLog[]

    this.storageData.forEach((item, index) => {
      this.attachToPool(item, index)
    })
    this.uploadFileFinished.subscribe(x => {
      const item: ImportLog = {
        Status: ImportStatus.PROCESSING,
        FileName: this.fileToUpload!.name,
        Key: x.key,
        StartedAt: "",
        FinishAt: "",
        TotalCreated: 0,
        TotalUpdated: 0,
        TotalFailed: 0,
        Created: Date.now().toString(),
        ExceptionsLog: [],
        isFinished: false,
        IsSuccess: false
      }

      this.storageData.push(item)
      localStorage.setItem(this.idenfifier, JSON.stringify(this.storageData));

      this.attachToPool(item, this.storageData.length - 1)
      this.fileToUpload = null

    })

    this.updatePaginatedErrors()
  }

  onPageChange(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.currentPage = event.pageIndex;
    this.updatePaginatedErrors()
  }

  updatePaginatedErrors() {
    const startIndex = this.currentPage * this.pageSize;
    this.paginatedErrors = this.errosPerRow.slice(startIndex, startIndex + this.pageSize);
  }

  processErrorsRows(error: string) {
    this.errosPerRow = [];
  
    const regex = /Linha (\d+): (.+?)(?=\n|$)/g;
    let match: string[] | null;
  
    while ((match = regex.exec(error)) !== null) {
      const numberRow = parseInt(match[1], 10);
      const erros = match[2].split(" | ").map(erro => erro.trim()); // 🔹 Agora dividindo corretamente pelos erros na mesma linha
  
      const rowErro = this.errosPerRow.find(l => l.Row === numberRow);
      if (rowErro) {
        rowErro.Erros.push(...erros);
      } else {
        this.errosPerRow.push({ Row: numberRow, Erros: erros });
      }
    }
  
    this.errosPerRow = [...this.errosPerRow]; // Força a atualização do Angular
    this.updatePaginatedErrors();
  }

  attachToPool(item: ImportLog, index: number) {
    const sub = interval(6000)
      .pipe(
        switchMap(() => this.importsStatusService.checkStatus(item.Key)),
        takeWhile(status => {
          if (this.storageData[index]?.isFinished) {
            return false;
          }
          return true;
        })
      ).subscribe((response: AsyncImportStatus) => {
        if (response.info?.exceptionLog !== null)
          this.processErrorsRows(response.info?.exceptionLog as string)

        this.storageData[index].StartedAt = response.startedAt
        this.storageData[index].Created = response.createdAt
        this.storageData[index].Status = response.status
        this.storageData[index].FinishAt = response.finishAt
        this.storageData[index].isFinished = response.isFinished()
        if (response.isFinished()) {
          this.storageData[index].TotalCreated = response.info.totalCreated
          this.storageData[index].TotalUpdated = response.info.totalUpdated
          this.storageData[index].TotalFailed = response.info.totalFailed

          if (response.info?.exceptionLog === null) {
            this.storageData[index].IsSuccess = true
          }
        }
        if (response.isFinished()) {
          sub.unsubscribe()
          this.cdr.detectChanges();
        }
      });

    this.pooling.push(sub)

  }

  onFileSelected(event: any) {
    this.fileToUpload = event.target.files[0] as File;
  }

  downloadTemplate() {
    this.downloadTemplateClicked.emit()
  }

  uploadFile() {
    if (this.fileToUpload) {
      this.uploadFileClicked.emit(this.fileToUpload);
    }
  }

  cleanCache() {
    this.storageData = []
    this.fileToUpload = null
    this.errosPerRow = []
    this.paginatedErrors = []
    localStorage.removeItem(this.idenfifier)
  }

  close() {
    if (this.storageData.length > 0 && this.storageData[0].isFinished) {
      this.cleanCache()
    }
    this.dialogRef.close()
  }

  exportErrorsUploadToCSV() {
    const csvData = this.errosPerRow
      .map(item => item.Erros.map(erro => `${item.Row};${erro}`).join('\n')) // Corrige o mapeamento dos erros
      .join('\n');
    const csvContext = `Linha;Erros\n${csvData}`;

    const blob = new Blob([csvContext], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)

    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'Limite_Beneficiarios_Erros.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}


export class ImportLog {
  Status!: ImportStatus | null;
  StartedAt!: string | null;
  FinishAt!: string | null;
  FileName!: string;
  TotalCreated!: number | null
  TotalUpdated!: number | null
  TotalFailed!: number | null
  Key!: string;
  Created!: string
  ExceptionsLog: RowError[]
  isFinished: boolean
  IsSuccess: boolean
}


export class Summary {
  TotalUpdated!: number
  TotalCreated!: number
  TotalRemoved!: number
  TotalFailed!: number
  Errors!: string | null
}

export class RowError {
  Row!: number;
  Erros!: string[]
}